import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  CardActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { TableSkeleton } from '../components/Loading';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import JurisdictionBadge from '../components/JurisdictionBadge/JurisdictionBadge';
import ListPageEmpty from '../components/ListPageEmpty/ListPageEmpty';

interface CompanyStats {
  totalCompanies: number;
  activeCompanies: number;
  jurisdictionCount: number;
  needsAttention: number;
  recentlyAdded: number;
  averageAge: number;
  byJurisdiction?: Array<{ jurisdiction: string; count: number }>;
  byStatus?: Array<{ status: string; count: number }>;
}

const Companies: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companies, setCompanies] = useState<any[]>([]);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dense, setDense] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [hasDirectorsFilter, setHasDirectorsFilter] = useState<boolean | null>(null);
  const [hasShareholdersFilter, setHasShareholdersFilter] = useState<boolean | null>(null);
  const [hasDocumentsFilter, setHasDocumentsFilter] = useState<boolean | null>(null);

  // Get unique jurisdictions from stats
  const jurisdictions = stats?.byJurisdiction?.map((j: any) => j.jurisdiction).filter(Boolean) || [];

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await companiesAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setLoadError('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const response = await companiesAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, []);

  const filteredCompanies = companies.filter((company) => {
    // Search filter
    const searchLower = search.toLowerCase();
    const matchesSearch = !search || 
      company.name?.toLowerCase().includes(searchLower) ||
      company.jurisdiction?.toLowerCase().includes(searchLower) ||
      company.registrationNumber?.toLowerCase().includes(searchLower);
    
    if (!matchesSearch) return false;
    
    // Status filter
    if (statusFilter !== 'ALL' && company.status !== statusFilter) {
      return false;
    }
    
    // Jurisdiction filter
    if (jurisdictionFilter.length > 0 && !jurisdictionFilter.includes(company.jurisdiction)) {
      return false;
    }
    
    // Advanced filters (placeholder - would need backend data for these)
    // These would typically come from related counts in the API response
    if (hasDirectorsFilter !== null) {
      // Would need director count from API
    }
    
    if (hasShareholdersFilter !== null) {
      // Would need shareholder count from API
    }
    
    if (hasDocumentsFilter !== null) {
      // Would need document count from API
    }
    
    return true;
  });
  
  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('ALL');
    setJurisdictionFilter([]);
    setHasDirectorsFilter(null);
    setHasShareholdersFilter(null);
    setHasDocumentsFilter(null);
  };
  
  const activeFilterCount =
    (search ? 1 : 0) +
    (statusFilter !== 'ALL' ? 1 : 0) +
    jurisdictionFilter.length +
    (hasDirectorsFilter !== null ? 1 : 0) +
    (hasShareholdersFilter !== null ? 1 : 0) +
    (hasDocumentsFilter !== null ? 1 : 0);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Company Name',
      flex: 2,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <RouterLink
          to={`/companies/${params.row.id}`}
          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
        >
          {params.value}
        </RouterLink>
      ),
    },
    {
      field: 'jurisdiction',
      headerName: 'Jurisdiction',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => <JurisdictionBadge jurisdiction={params.value} size="small" />,
    },
    {
      field: 'registrationNumber',
      headerName: 'Registration #',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'incorporationDate',
      headerName: 'Incorporated',
      width: 130,
      valueFormatter: (params) => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  const renderMobileCards = () => {
    if (loading) {
      return <TableSkeleton rows={6} />;
    }
    if (loadError) return null;
    if (filteredCompanies.length === 0) {
      return (
        <Box sx={{ height: 400 }}>
          <ListPageEmpty
            message={activeFilterCount > 0 ? 'No companies match your filters' : 'No companies yet'}
            secondary={activeFilterCount > 0 ? 'Clear filters to see all companies.' : undefined}
            hasActiveFilters={activeFilterCount > 0}
            onClearFilters={handleClearFilters}
            icon={<BusinessIcon />}
          />
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ pb: 2 }}>
        {filteredCompanies.map((company) => (
          <Card
            key={company.id}
            sx={{
              borderRadius: 2,
              '&:hover': { boxShadow: 3 },
            }}
          >
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  component={RouterLink}
                  to={`/companies/${company.id}`}
                  sx={{ textDecoration: 'none', color: 'primary.main', flex: 1, mr: 1 }}
                >
                  {company.name}
                </Typography>
                <JurisdictionBadge jurisdiction={company.jurisdiction} size="small" />
              </Box>

              <Stack spacing={1}>
                {company.registrationNumber && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                      Registration #:
                    </Typography>
                    <Typography variant="body2">{company.registrationNumber}</Typography>
                  </Box>
                )}
                {company.status && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                      Status:
                    </Typography>
                    <Chip
                      label={company.status}
                      size="small"
                      color={
                        company.status === 'ACTIVE' ? 'success' :
                        company.status === 'DORMANT' ? 'warning' :
                        company.status === 'DISSOLVED' || company.status === 'WOUND_UP' ? 'error' :
                        'default'
                      }
                      variant="outlined"
                    />
                  </Box>
                )}
                {company.incorporationDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                      Incorporated:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(company.incorporationDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to={`/companies/${company.id}`}
                endIcon={<OpenInNewIcon />}
                fullWidth
              >
                View Company
              </Button>
            </CardActions>
          </Card>
        ))}
      </Stack>
    );
  };

  const statCards = [
    {
      title: 'Total Companies',
      value: stats?.totalCompanies || 0,
      icon: <BusinessIcon sx={{ fontSize: 36 }} />,
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: 'Active',
      value: stats?.activeCompanies || 0,
      icon: <CheckCircleIcon sx={{ fontSize: 36 }} />,
      color: '#2e7d32',
      bgGradient: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    },
    {
      title: 'Jurisdictions',
      value: stats?.jurisdictionCount || 0,
      icon: <PublicIcon sx={{ fontSize: 36 }} />,
      color: '#0288d1',
      bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      title: 'Needs Attention',
      value: stats?.needsAttention || 0,
      icon: <WarningIcon sx={{ fontSize: 36 }} />,
      color: '#ed6c02',
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      title: 'Recently Added',
      value: stats?.recentlyAdded || 0,
      suffix: 'last 30 days',
      icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
      color: '#9c27b0',
      bgGradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
      title: 'Average Age',
      value: stats?.averageAge || 0,
      suffix: 'years',
      icon: <CalendarTodayIcon sx={{ fontSize: 36 }} />,
      color: '#d32f2f',
      bgGradient: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Companies
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and view all companies in your portfolio
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
        ) : (
          statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: card.bgGradient,
                  color: 'white',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        width: 56,
                        height: 56,
                        mb: 2,
                      }}
                    >
                      {card.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    {card.suffix && (
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5 }}>
                        {card.suffix}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack spacing={2}>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search companies by name, jurisdiction, or registration number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Quick Filters */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon fontSize="small" />
              Quick Filters
              {activeFilterCount > 0 && (
                <Chip label={`${activeFilterCount} active`} size="small" color="primary" />
              )}
            </Typography>
            
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mt: 1 }}>
              {/* Status Filter Chips */}
              <Chip
                label="All"
                onClick={() => setStatusFilter('ALL')}
                color={statusFilter === 'ALL' ? 'primary' : 'default'}
                variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Active"
                icon={<CheckCircleIcon />}
                onClick={() => setStatusFilter('ACTIVE')}
                color={statusFilter === 'ACTIVE' ? 'success' : 'default'}
                variant={statusFilter === 'ACTIVE' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Dormant"
                onClick={() => setStatusFilter('DORMANT')}
                color={statusFilter === 'DORMANT' ? 'warning' : 'default'}
                variant={statusFilter === 'DORMANT' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Dissolved"
                onClick={() => setStatusFilter('DISSOLVED')}
                color={statusFilter === 'DISSOLVED' ? 'error' : 'default'}
                variant={statusFilter === 'DISSOLVED' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Wound Up"
                onClick={() => setStatusFilter('WOUND_UP')}
                color={statusFilter === 'WOUND_UP' ? 'error' : 'default'}
                variant={statusFilter === 'WOUND_UP' ? 'filled' : 'outlined'}
              />
              
              {/* Jurisdiction Filter */}
              <Autocomplete
                multiple
                options={jurisdictions}
                value={jurisdictionFilter}
                onChange={(_, newValue) => setJurisdictionFilter(newValue)}
                size="small"
                sx={{ minWidth: isMobile ? '100%' : 250 }}
                renderInput={(params) => (
                  <TextField {...params} label="Jurisdictions" placeholder="Select..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />

              {/* Clear Filters Button */}
              {activeFilterCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              )}
            </Stack>
          </Box>

          {/* Advanced Filters */}
          <Accordion
            expanded={showAdvancedFilters}
            onChange={() => setShowAdvancedFilters(!showAdvancedFilters)}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Advanced Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Has Directors</InputLabel>
                    <Select
                      value={hasDirectorsFilter === null ? '' : hasDirectorsFilter ? 'yes' : 'no'}
                      label="Has Directors"
                      onChange={(e) => {
                        const value = e.target.value;
                        setHasDirectorsFilter(value === '' ? null : value === 'yes');
                      }}
                    >
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Has Shareholders</InputLabel>
                    <Select
                      value={hasShareholdersFilter === null ? '' : hasShareholdersFilter ? 'yes' : 'no'}
                      label="Has Shareholders"
                      onChange={(e) => {
                        const value = e.target.value;
                        setHasShareholdersFilter(value === '' ? null : value === 'yes');
                      }}
                    >
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Has Documents</InputLabel>
                    <Select
                      value={hasDocumentsFilter === null ? '' : hasDocumentsFilter ? 'yes' : 'no'}
                      label="Has Documents"
                      onChange={(e) => {
                        const value = e.target.value;
                        setHasDocumentsFilter(value === '' ? null : value === 'yes');
                      }}
                    >
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Results Count and density toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredCompanies.length} of {companies.length} companies
              {companies.length >= 5000 && ' – refine filters to narrow results'}
            </Typography>
            <Chip
              label={dense ? 'Compact' : 'Standard'}
              size="small"
              variant={dense ? 'filled' : 'outlined'}
              onClick={() => setDense(!dense)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Stack>
      </Paper>

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadCompanies}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {loadError}
        </Alert>
      )}

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <TableSkeleton rows={10} />
          ) : loadError ? null : filteredCompanies.length === 0 ? (
            <ListPageEmpty
              message={activeFilterCount > 0 ? 'No companies match your filters' : 'No companies yet'}
              secondary={activeFilterCount > 0 ? 'Clear filters to see all companies.' : undefined}
              hasActiveFilters={activeFilterCount > 0}
              onClearFilters={handleClearFilters}
              icon={<BusinessIcon />}
            />
          ) : (
            <DataGrid
              rows={filteredCompanies}
              columns={columns}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              disableRowSelectionOnClick
              density={dense ? 'compact' : 'standard'}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          )}
        </Paper>
      )}
    </Container>
  );
};

export default Companies;
